import type { Party } from '../types/case';

export interface ResolvedSpeaker {
  label: string;
  name: string;
  isMediator: boolean;
  partyId?: string;
}

const DEEPGRAM_LABEL = /speaker_(\d+)/i;

/**
 * Map a Deepgram-style `speaker_N` label to the human name we should show.
 *
 * V1 heuristic (matches Phase 2 spec §4.3 "connection order"):
 *   - speaker_0 → "Mediator" (the app user — never a party row)
 *   - speaker_N (N≥1) → the Nth party in creation order
 *
 * Manual speaker correction will refine this in a later phase; for now the
 * mapping is enough to render party names in the UI and in LLM-emitted
 * reference quotes.
 */
export function resolveSpeaker(label: string, parties: Party[]): ResolvedSpeaker {
  const match = DEEPGRAM_LABEL.exec(label);
  if (!match) {
    return { label, name: label, isMediator: false };
  }
  const idx = Number(match[1]);
  if (idx === 0) {
    return { label, name: 'Mediator', isMediator: true };
  }
  const sorted = [...parties].sort(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
  );
  const party = sorted[idx - 1];
  if (party) {
    return { label, name: party.name, isMediator: false, partyId: party.id };
  }
  return { label, name: `Speaker ${idx + 1}`, isMediator: false };
}

/**
 * Replace occurrences of `speaker_N` inside free-form text (e.g. an LLM
 * reference quote) with the human-readable name from `resolveSpeaker`.
 */
export function humanizeSpeakerRefs(text: string, parties: Party[]): string {
  return text.replace(/speaker_(\d+)/gi, (raw) => resolveSpeaker(raw, parties).name);
}
